import axios from 'axios';
import JSON5 from 'json5';
import { beginStartupCheck, finishStartupCheck } from './startupValidation';

/* global siteSubdirectory */

export const addSubdirectoryToPath = (path) => (typeof siteSubdirectory === 'string' ? siteSubdirectory + path : path);

const getConfigVariant = () => {
  const queryConfigVariant = new URLSearchParams(window.location.search).get('config');
  if (queryConfigVariant !== null) {
    return { configVariant: queryConfigVariant, fromStorage: false };
  }

  try {
    const configVariant = sessionStorage.getItem('config');
    return { configVariant, fromStorage: configVariant !== null };
  } catch (error) {
    return { configVariant: null, fromStorage: false };
  }
};

const storeConfigVariant = (configVariant) => {
  try {
    sessionStorage.setItem('config', configVariant);
    return true;
  } catch (error) {
    // Session storage can be unavailable in restricted browser contexts.
    return false;
  }
};

const removeConfigQueryParameter = () => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('config');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch (error) {
    // Keep the URL unchanged if the browser does not allow history updates.
  }
};

const loadConfig = async (location) => {
  // Read the response as text so Axios does not try strict JSON parsing first.
  const response = await axios.get(addSubdirectoryToPath(location), { responseType: 'text' });
  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Unhandled HTTP status: ${response.status}`);
  }

  let appConfig = response.data;

  if (typeof appConfig === 'string') {
    try {
      appConfig = JSON5.parse(appConfig);
    } catch (error) {
      throw new Error(`Config response is not valid JSON5: ${error.message}`);
    }
  }

  if (!appConfig || typeof appConfig !== 'object' || Array.isArray(appConfig)) {
    throw new Error('Config response must be a JSON5 object');
  }

  return appConfig;
};

const loadConfiguredAppConfig = async (defaultLocation) => {
  const { configVariant, fromStorage } = getConfigVariant();
  const configLocation = configVariant && configVariant !== 'default'
    ? defaultLocation.replace(/(\.json|\.js)$/i, `.${configVariant}$1`)
    : defaultLocation;

  try {
    const appConfig = await loadConfig(configLocation);
    if (configVariant !== null && storeConfigVariant(configVariant)) {
      const queryHasConfig = new URLSearchParams(window.location.search).has('config');
      if (queryHasConfig) {
        removeConfigQueryParameter();
      }
    }
    return appConfig;
  } catch (error) {
    if (configLocation !== defaultLocation) {
      console.warn(`*** custom config file could not be loaded; falling back to ${defaultLocation}.`, error);
      if (fromStorage) {
        try {
          sessionStorage.removeItem('config');
        } catch (storageError) {
          // Session storage can be unavailable in restricted browser contexts.
        }
      }
      try {
        return await loadConfig(defaultLocation);
      } catch (fallbackError) {
        console.error('*** local config file could not be loaded. Add appConfig.json or the configured APP_CONFIG_LOCATION file to use the application.\nSee errors for more details: ', fallbackError);
        return undefined;
      }
    }

    console.error('*** local config file could not be loaded. Add appConfig.json or the configured APP_CONFIG_LOCATION file to use the application.\nSee errors for more details: ', error);
    return undefined;
  }
};

const updateConfig = async (defaultLocation) => {
  beginStartupCheck('applicationConfig', 'Application configuration');
  let succeeded = false;

  try {
    const appConfig = await loadConfiguredAppConfig(defaultLocation);
    succeeded = Boolean(appConfig && typeof appConfig === 'object' && !Array.isArray(appConfig));
    return appConfig;
  } finally {
    finishStartupCheck('applicationConfig', succeeded);
  }
};

export default updateConfig;
